/**
 * Tenant Detection Utility
 * Detects tenant from URL (subdomain or custom domain)
 */

import { API_URL } from '../config/api';


/**
 * Extract subdomain from current hostname
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  console.log(`🔍 [getSubdomain] Processing hostname: ${hostname}`);
  
  // Handle localhost with subdomain (e.g., tenant.localhost:3001)
  if (hostname.includes('localhost')) {
    const parts = hostname.split('.');
    console.log(`🔍 [getSubdomain] Localhost parts:`, parts);
    if (parts.length > 1 && parts[0] !== 'localhost') {
      let subdomain = parts[0];
      // Handle admin suffix: shilpani-admin -> shilpani
      if (subdomain.endsWith('-admin')) {
        console.log(`🔍 [getSubdomain] Removing -admin suffix from: ${subdomain}`);
        subdomain = subdomain.replace('-admin', '');
      }
      console.log(`🔍 [getSubdomain] Localhost result: ${subdomain}`);
      return subdomain;
    }
    return null;
  }
  
  // Handle production domains (e.g., tenant.yoursaas.com, tenant-admin.yoursaas.com)
  const parts = hostname.split('.');
  console.log(`🔍 [getSubdomain] Production parts:`, parts);
  if (parts.length > 2) {
    let subdomain = parts[0];
    // Handle admin suffix: shilpani-admin -> shilpani
    if (subdomain.endsWith('-admin')) {
      console.log(`🔍 [getSubdomain] Removing -admin suffix from: ${subdomain}`);
      subdomain = subdomain.replace('-admin', '');
    }
    console.log(`🔍 [getSubdomain] Production result: ${subdomain}`);
    return subdomain;
  }
  
  console.log(`🔍 [getSubdomain] No subdomain found`);
  return null;
};

/**
 * Extract tenant identifier from URL path
 * Supports URLs like /shilpani/orders -> returns 'shilpani'
 */
export const getTenantFromPath = () => {
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean);
  
  // Check if first path segment could be a tenant identifier
  // Exclude admin panel routes that shouldn't be mistaken for tenant names
  const excludedPaths = [
    // Admin panel routes
    'login', 'orders', 'coupons', 'modules', 'upload', 'categories', 
    'products', 'attributes', 'banners', 'blogs', 'workshops', 'seo', 
    'reviews', 'automation', 'ai-config', 'workflows', 'settings', 
    'theme', 'users', 'billing',
    // Common routes
    'api', 'static', 'assets', 'public'
  ];
  
  if (pathParts.length > 0 && !excludedPaths.includes(pathParts[0].toLowerCase())) {
    return pathParts[0];
  }
  
  return null;
};

/**
 * Get current tenant based on hostname or path
 * Priority: subdomain > path
 * Logic: 
 * - For bigfat.ai domain: use subdomain detection
 * - For vercel.app domain: use path-based detection
 * - For localhost: support both subdomain and path-based detection
 */
export const getCurrentTenant = () => {
  const hostname = window.location.hostname;
  console.log(`🔍 [getCurrentTenant] Starting with hostname: ${hostname}`);
  
  const isVercelDomain = hostname.endsWith('.vercel.app');
  const isBigfatDomain = hostname.endsWith('.bigfat.ai') || hostname === 'bigfat.ai';
  const isLocalhost = hostname === 'localhost' || 
                      hostname === '127.0.0.1' ||
                      hostname.endsWith('.localhost');
  
  console.log(`🔍 [getCurrentTenant] Domain checks:`, {
    isVercelDomain,
    isBigfatDomain,
    isLocalhost
  });
  
  if (isVercelDomain) {
    // Vercel domains - use path-based detection
    console.log(`🔍 [getCurrentTenant] Using Vercel path detection`);
    return getTenantFromPath();
  } else if (isBigfatDomain) {
    // Bigfat.ai domain - use subdomain-based detection
    console.log(`🔍 [getCurrentTenant] Using Bigfat subdomain detection`);
    return getSubdomain();
  } else if (isLocalhost) {
    // Localhost - support both subdomain and path-based detection
    // Try subdomain first, then path
    console.log(`🔍 [getCurrentTenant] Using localhost (subdomain first, then path)`);
    const subdomain = getSubdomain();
    if (subdomain) {
      console.log(`🔍 [getCurrentTenant] Found localhost subdomain: ${subdomain}`);
      return subdomain;
    }
    const pathTenant = getTenantFromPath();
    console.log(`🔍 [getCurrentTenant] Using localhost path: ${pathTenant}`);
    return pathTenant;
  } else {
    // Default: try subdomain first, then path
    console.log(`🔍 [getCurrentTenant] Using default detection`);
    const subdomain = getSubdomain();
    if (subdomain) return subdomain;
    return getTenantFromPath();
  }
};

/**
 * Get the base path for routing (includes tenant path if present)
 */
export const getBasePath = () => {
  const tenantPath = getTenantFromPath();
  return tenantPath ? `/${tenantPath}` : '';
};

/**
 * Get current hostname
 */
export const getHostname = () => {
  return window.location.hostname;
};

/**
 * Fetch tenant info from backend
 */
export const fetchTenantInfo = async () => {
  try {
    // Try to get tenant identifier from path first, then subdomain
    const tenantIdentifier = getTenantFromPath() || getSubdomain();
    
    // Use axios if available, otherwise use fetch
    const useAxios = typeof window !== 'undefined' && window.axios;
    
    if (useAxios) {
      const axios = window.axios;
      try {
        const response = await axios.get(`${API_URL}/api/public/tenant`, {
          headers: {
            'Host': window.location.hostname,
            ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
          }
        });
        return response.data.tenant;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    } else {
      const response = await fetch(`${API_URL}/api/public/tenant`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Host': window.location.hostname,
          ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No tenant found - this is OK for default/fallback behavior
          return null;
        }
        throw new Error('Failed to fetch tenant info');
      }
      
      const data = await response.json();
      return data.tenant;
    }
  } catch (error) {
    console.error('Error fetching tenant info:', error);
    return null;
  }
};

/**
 * Initialize tenant context
 * This should be called when the app loads
 */
export const initializeTenant = async () => {
  const tenant = await fetchTenantInfo();
  
  if (tenant) {
    // Store tenant info in localStorage for quick access
    localStorage.setItem('tenant', JSON.stringify({
      id: tenant.id,
      name: tenant.name,
      subdomain: tenant.subdomain,
      customDomain: tenant.customDomain,
      frontendUrl: tenant.frontendUrl,
      status: tenant.status,
      plan: tenant.plan
    }));
    
    // Set tenant ID in axios default headers if using axios
    if (window.axios) {
      window.axios.defaults.headers.common['X-Tenant-Id'] = tenant.id;
    }
    
    return tenant;
  }
  
  // Clear tenant from localStorage if not found
  localStorage.removeItem('tenant');
  return null;
};

/**
 * Get tenant from localStorage (synchronous)
 */
export const getTenant = () => {
  try {
    const tenantStr = localStorage.getItem('tenant');
    if (tenantStr) {
      return JSON.parse(tenantStr);
    }
  } catch (error) {
    console.error('Error reading tenant from localStorage:', error);
  }
  return null;
};

/**
 * Check if current URL matches tenant's frontend URL
 */
export const isTenantUrl = (tenant) => {
  if (!tenant || !tenant.frontendUrl) {
    return false;
  }
  
  const currentUrl = window.location.origin;
  const tenantUrl = new URL(tenant.frontendUrl).origin;
  
  return currentUrl === tenantUrl;
};

